<template>
    <div class="bg-bluish">
        <!-- HERO -->
        <section class="relative min-h-screen overflow-hidden bg-[#050505] text-white">
            <!-- background -->
            <div class="absolute inset-0">
                <div
                    class="h-full w-full bg-[url('@img/backgrounds/pattern-3.webp')] bg-cover bg-center opacity-60"
                ></div>

                <div class="absolute inset-0 bg-black/70"></div>

                <div
                    class="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,196,0,0.14),transparent_30%),radial-gradient(circle_at_85%_18%,rgba(255,196,0,0.1),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.04),transparent_26%)]"
                ></div>

                <!-- glow blobs -->
                <div class="absolute -left-24 top-24 h-[260px] w-[260px] rounded-full bg-baseColor/10 blur-3xl"></div>
                <div
                    class="absolute right-[-80px] top-[-40px] h-[260px] w-[260px] rounded-full bg-baseColor/10 blur-3xl"></div>

                <div class="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0d141b] to-transparent"></div>
            </div>

            <div class="relative container flex min-h-screen flex-col justify-center pt-[90px] pb-[40px] md:pt-20">
                <div class="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
                    <!-- LEFT -->
                    <div class="lg:col-span-7">
                        <Breadcrumbs theme="dark"/>
                        <!-- badge -->
                        <div
                            class="mb-2 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2">
                            <span
                                class="h-2 w-2 rounded-full bg-baseColor shadow-[0_0_18px_rgba(255,196,0,0.8)]"></span>
                            <p class="text-xs font-semibold uppercase tracking-[0.1em] text-white/65">
                                Внедрение Bitrix под ваш бизнес
                            </p>
                        </div>

                        <!-- title -->
                        <h1 class="max-w-4xl text-2xl font-extrabold leading-[1.04] tracking-[-0.04em] md:text-4xl">
                            Внедрение Bitrix24
                            <span class="text-baseColor">под бизнес-процессы</span>
                            компании
                        </h1>

                        <!-- description -->
                        <p class="quote mt-6 max-w-3xl text-xs leading-[1.5] text-white/82 md:text-sm lg:text-[15px]">
                            Полноценный корпоративный портал для коммуникаций,
                            управления задачами, контроля продаж и прозрачной работы
                            с клиентами. Настраиваем систему под реальные процессы бизнеса.
                        </p>

                        <!-- chips -->
                        <div class="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                            <div
                                class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/75">
                                <span class="h-1.5 w-1.5 rounded-full bg-baseColor"></span>
                                CRM и сделки
                            </div>

                            <div
                                class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/75">
                                <span class="h-1.5 w-1.5 rounded-full bg-baseColor"></span>
                                Автоматизация процессов
                            </div>

                            <div
                                class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/75">
                                <span class="h-1.5 w-1.5 rounded-full bg-baseColor"></span>
                                Интеграции и аналитика
                            </div>
                        </div>

                        <!-- CTA -->
                        <div class="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div
                                @click.prevent="callbackStore.showBaseCallbackPopup({ title: 'Внедрение Bitrix24', description: 'Внедрение Bitrix24' })"
                                class="inline-flex h-[54px] w-full cursor-pointer items-center justify-center rounded-2xl bg-baseColor px-6 text-[16px] font-bold text-black shadow-[0_12px_40px_rgba(255,196,0,0.22)] transition hover:-translate-y-0.5 hover:bg-[#e6b000] sm:w-auto sm:min-w-[230px]"
                            >
                                Оставить заявку
                            </div>

                            <div class="flex items-center gap-3 text-sm text-white/55">
                                <span class="h-px w-10 bg-white/15"></span>
                                Аудит процессов и план внедрения
                            </div>
                        </div>

                        <!-- metrics -->
                        <div class="max-md:hidden mt-12 grid max-w-[820px] grid-cols-1 gap-4 sm:grid-cols-3">
                            <div
                                v-for="(item, index) in [
                                    'Прозрачность всех процессов',
                                    'Контроль задач и сотрудников',
                                    'Рост эффективности продаж'
                                ]"
                                :key="item"
                                class="group relative overflow-hidden rounded-[24px] border border-white/10 bg-white/[0.04] p-5 transition duration-300 hover:-translate-y-1 hover:border-baseColor/30 hover:bg-white/[0.06]"
                            >
                                <div
                                    class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-baseColor/70 to-transparent"></div>

                                <div class="text-[34px] font-extrabold leading-none text-baseColor">
                                    {{ String(index + 1).padStart(2, '0') }}
                                </div>

                                <p class="mt-3 text-[15px] leading-[1.5] text-white/82">
                                    {{ item }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- RIGHT -->
                    <div class="lg:col-span-5">
                        <div class="relative">
                            <div
                                class="absolute -inset-3 rounded-[34px] bg-gradient-to-br from-baseColor/15 via-transparent to-white/5 blur-2xl"></div>

                            <div
                                class="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/[0.05] backdrop-blur-sm p-4">

                                <!-- header -->
                                <div
                                    class="mb-4 flex items-center justify-between rounded-[24px] border border-white/10 bg-black/20 px-4 py-3">
                                    <div>
                                        <div class="text-xs uppercase tracking-[0.14em] text-white/40">
                                            Система управления
                                        </div>
                                        <div class="mt-1 text-lg font-extrabold text-white">
                                            Bitrix24 под контролем
                                        </div>
                                    </div>

                                    <div
                                        class="inline-flex h-12 w-24 items-center justify-center rounded-2xl border border-white/10 bg-white font-bold text-black">
                                        <IconPartnerBitrix24 class="h-12 w-24 px-2 py-1"/>
                                    </div>
                                </div>

                                <!-- cards -->
                                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                                    <div
                                        class="group relative overflow-hidden rounded-[26px] border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:border-baseColor/25 hover:bg-white/[0.06]">
                                        <div
                                            class="absolute right-0 top-0 h-24 w-24 rounded-full bg-baseColor/10 blur-2xl"></div>

                                        <div
                                            class="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/42">
                                            CRM и продажи
                                        </div>

                                        <div class="mt-3 text-[22px] font-extrabold leading-[1.2] text-white">
                                            Единая база клиентов и сделок
                                        </div>
                                    </div>

                                    <div
                                        class="group relative overflow-hidden rounded-[26px] border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:border-baseColor/25 hover:bg-white/[0.06]">
                                        <div
                                            class="absolute right-0 top-0 h-24 w-24 rounded-full bg-baseColor/10 blur-2xl"></div>

                                        <div
                                            class="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/42">
                                            Автоматизация
                                        </div>

                                        <div class="mt-3 text-[22px] font-extrabold leading-[1.2] text-white">
                                            Роботы и бизнес-процессы
                                        </div>
                                    </div>

                                    <div
                                        class="group relative overflow-hidden rounded-[26px] border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:border-baseColor/25 hover:bg-white/[0.06] sm:col-span-2 lg:col-span-1">
                                        <div
                                            class="absolute right-0 top-0 h-24 w-24 rounded-full bg-baseColor/10 blur-2xl"></div>

                                        <div
                                            class="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/42">
                                            Интеграции
                                        </div>

                                        <div class="mt-3 text-[22px] font-extrabold leading-[1.2] text-white">
                                            Телефония, мессенджеры и сервисы
                                        </div>
                                    </div>
                                </div>

                                <!-- bottom -->
                                <div class="mt-4 rounded-[26px] border border-white/10 bg-black/20 p-5">
                                    <div class="text-xs uppercase tracking-[0.14em] text-white/38">
                                        Результат
                                    </div>

                                    <div class="mt-2 text-xs leading-[1.5] text-white/72 md:text-sm">
                                        Система, которая реально управляет бизнесом:
                                        от первого контакта до закрытия сделки и повторных продаж.
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>

        <!-- ДЛЯ ЧЕГО НУЖЕН -->
        <section class="relative overflow-hidden py-10 md:py-20">
            <div class="absolute inset-0 opacity-50">
                <img
                    src="@img/backgrounds/pattern-2.webp"
                    alt=""
                    class="h-full w-full object-cover"
                />
            </div>

            <div class="relative container">
                <p
                    class="mb-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#7d8794] md:mb-3 md:text-sm">
                    Единая база всех клиентов, задач и сделок
                </p>

                <div class="mb-10 flex items-center gap-6 md:mb-14">
                    <h2
                        class="shrink-0 text-2xl font-extrabold uppercase leading-none tracking-[-0.04em] text-[#111111] md:text-4xl"
                    >
                        Для чего нужен Bitrix24
                    </h2>
                    <div class="hidden h-px w-full bg-[#c8d0d8] md:block"></div>
                </div>

                <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    <div
                        v-for="item in needs"
                        :key="item.title"
                        class="rounded-[28px] bg-white p-7 shadow-sm"
                    >
                        <div class="mb-6 flex items-center gap-4">
                            <div class="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-bluish">
                                <span class="block h-3.5 w-3.5 rounded-full bg-baseColor"></span>
                            </div>

                            <h3 class="text-[22px] font-extrabold leading-[1.25] text-[#173047]">
                                {{ item.title }}
                            </h3>
                        </div>

                        <p class="text-sm leading-[1.5] text-[#587086] md:text-[15px]">
                            {{ item.text }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ПРЕИМУЩЕСТВА -->
        <section class="py-10 md:pb-20">
            <div class="container">
                <div class="mb-10 flex items-center gap-6 md:mb-14">
                    <h2
                        class="shrink-0 text-2xl font-extrabold uppercase leading-none tracking-[-0.04em] text-[#111111] md:text-4xl">
                        Наши преимущества
                    </h2>
                    <div class="hidden h-px w-full bg-[#c8d0d8] md:block"></div>
                </div>

                <div class="grid grid-cols-1 gap-5 lg:grid-cols-3">
                    <div
                        v-for="(item, index) in advantages"
                        :key="item.title"
                        class="rounded-[28px] bg-white bg-center bg-no-repeat p-7 shadow-sm"
                        style="background-image: url('@img/backgrounds/pattern-2.webp');"
                    >
                        <div class="mb-5 flex items-center gap-4">
                            <div
                                class="flex h-[58px] min-w-[58px] items-center justify-center rounded-full bg-baseColor text-2xl font-extrabold text-black">
                                {{ String(index + 1).padStart(2, '0') }}
                            </div>

                            <h3 class="pt-1 text-base font-extrabold leading-[1.35] text-[#20262d] md:text-lg">
                                {{ item.title }}
                            </h3>
                        </div>

                        <p class="text-sm leading-[1.5] text-[#6b7d8e] md:text-[15px]">
                            {{ item.text }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ИНТЕГРАЦИИ -->
        <section class="py-10 md:pb-20">
            <div class="container">
                <p
                    class="mb-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#7d8794] md:mb-3 md:text-sm"
                >
                    Неограниченные возможности интеграций
                </p>

                <div class="mb-10 flex items-center gap-6 md:mb-14">
                    <h2
                        class="text-2xl font-extrabold uppercase leading-none tracking-[-0.04em] text-[#111111] md:text-4xl">
                        Интегрируем Bitrix24 с любыми системами
                    </h2>
                </div>

                <div class="grid grid-cols-2 gap-x-8 gap-y-10 xl:grid-cols-4">
                    <div v-for="group in integrations" :key="group.title">
                        <h3 class="mb-5 text-lg font-extrabold text-[#173047] md:text-xl">
                            {{ group.title }}
                        </h3>

                        <ul class="space-y-3">
                            <li v-for="item in group.items"
                                :key="item.title"
                                class="flex items-center gap-2 text-sm leading-[1.5] text-[#5e7386] md:text-[15px]">
                                <img v-if="item.icon"
                                     :src="item.icon"
                                     :alt="item.title"
                                     class="h-5 w-5 object-contain"/>
                                <span>{{ item.title }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <!-- ЭТАПЫ -->
        <section class="bg-white py-10 md:py-20">
            <div class="container">
                <div class="mb-10 flex items-center gap-6 md:mb-14">
                    <h2
                        class="shrink-0 text-2xl font-extrabold uppercase leading-none tracking-[-0.04em] text-[#111111] md:text-4xl"
                    >
                        Как проходит внедрение
                    </h2>
                    <div class="hidden h-px w-full bg-[#c8d0d8] md:block"></div>
                </div>

                <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                    <div
                        v-for="(step, index) in steps"
                        :key="step.title"
                        class="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-[#dfe6ec] bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-baseColor/30 hover:shadow-md"
                    >
                        <!-- верхний акцент -->
                        <div class="absolute inset-x-0 top-0 h-1 bg-baseColor/90"></div>

                        <!-- декоративное пятно -->
                        <div
                            class="absolute right-0 top-0 h-24 w-24 rounded-full bg-baseColor/10 blur-2xl transition duration-300 group-hover:bg-baseColor/15"
                        ></div>

                        <!-- номер -->
                        <div class="relative">
                            <div
                                class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-baseColor/10 text-2xl font-extrabold leading-none tracking-tight text-baseColor"
                            >
                                {{ String(index + 1).padStart(2, '0') }}
                            </div>
                        </div>

                        <!-- заголовок -->
                        <h3
                            class="relative mt-5 text-lg font-extrabold leading-tight text-[#172f45] md:text-xl"
                        >
                            {{ step.title }}
                        </h3>

                        <!-- текст -->
                        <p class="relative mt-3 text-sm leading-[1.5] text-[#63788b]">
                            {{ step.text }}
                        </p>

                        <!-- нижняя линия -->
                        <div class="mt-auto flex items-center gap-2 pt-4">
                            <div class="h-1.5 w-1.5 rounded-full bg-baseColor"></div>
                            <div class="h-px flex-1 bg-[#e3e8ee]"></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="bg-white py-10 md:pb-20">
            <div class="container">
                <div
                    class="relative overflow-hidden rounded-3xl bg-bluish bg-[url('@img/backgrounds/pattern-2.webp')] bg-cover bg-center bg-no-repeat shadow-xl">
                    <div
                        class="relative grid grid-cols-1 gap-8 px-6 py-8 md:px-10 md:py-10 lg:grid-cols-[minmax(0,1.4fr)_auto] lg:items-center lg:gap-10 xl:px-12"
                    >
                        <div class="max-w-3xl">
                            <h2 class="text-2xl font-extrabold leading-tight tracking-tight text-[#173047] md:text-3xl lg:text-4xl">
                                Готовы внедрить Bitrix24 под ваш бизнес?
                            </h2>

                            <p class="mt-4 max-w-2xl text-sm leading-[1.5] text-[#5f7385] md:text-base">
                                Бесплатно разберём ваши текущие процессы, подскажем, как
                                выстроить CRM, автоматизацию и интеграции, а также обозначим
                                стоимость, этапы и сроки внедрения под ваш бизнес.
                            </p>
                        </div>

                        <div class="flex lg:justify-end">
                            <button
                                @click.prevent="callbackStore.showBaseCallbackPopup({ title: 'Внедрение Bitrix24', description: 'Внедрение Bitrix24' })"
                                class="inline-flex h-14 w-full items-center justify-center rounded-2xl bg-baseColor px-6 text-sm font-bold uppercase tracking-wide text-black shadow-md transition hover:bg-baseColor/80 hover:shadow-lg sm:w-auto sm:min-w-72"
                            >
                                Оставить заявку
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { useCallbackStore } from "@js/store/CallbackStore.js";
import IconPartnerBitrix24 from "@js/components/Icons/IconPartnerBitrix24.vue";
import avitoIcon from "@img/brands/favicons/avito.webp";
import yandexDirectIcon from "@img/brands/favicons/yandex_direct.webp";
import whatsappIcon from "@img/brands/favicons/whatsapp.webp";
import telegramIcon from "@img/brands/favicons/telegram.webp";
import jivoIcon from "@img/brands/favicons/jivo.webp";
import wazzupIcon from "@img/brands/favicons/wazzup24.webp";
import megafonIcon from "@img/brands/favicons/megafon.webp";
import maxIcon from "@img/brands/favicons/max.webp";
import oneCIcon from "@img/brands/favicons/1c.webp";
import mangoIcon from "@img/brands/favicons/mango.webp";
import uisIcon from "@img/brands/favicons/uis.webp";
import yandexMetrikaIcon from "@img/brands/favicons/yandex_metrika.webp";
import roistatIcon from "@img/brands/favicons/roistat.webp";
import callTouchIcon from "@img/brands/favicons/calltouch.webp";
import googleDataStudioIcon from "@img/brands/favicons/googleDataStudio.webp";
import tBankIcon from "@img/brands/favicons/tbank.webp";
import sberIcon from "@img/brands/favicons/sber.webp";
import alphaBankIcon from "@img/brands/favicons/alphabank.webp";
import yookassaIcon from "@img/brands/favicons/yookassa.webp";
import robokassaIcon from "@img/brands/favicons/robokassa.webp";
import yandexDiskIcon from "@img/brands/favicons/YandexDisk.webp";
import googleDriveIcon from "@img/brands/favicons/googleDrive.webp";
import dropboxIcon from "@img/brands/favicons/dropbox.webp";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";

const callbackStore = useCallbackStore();

const needs = [
    {
        title: "Лиды и сделки",
        text: "Не теряйте ни одной заявки. Все источники обращений, история коммуникаций и работа менеджеров фиксируются в одной системе.",
    },
    {
        title: "Бизнес-процессы",
        text: "Автоматизируйте рутинные действия: шаблонные письма, постановку задач, согласования, документы и внутренние сценарии работы.",
    },
    {
        title: "Задачи и проекты",
        text: "Bitrix24 помогает контролировать сроки, распределять ответственность и видеть реальную загрузку команды по каждому проекту.",
    },
    {
        title: "Воронка продаж",
        text: "Вы видите, сколько клиентов находится в работе, на какой стадии сделки они находятся и где бизнес теряет заявки.",
    },
    {
        title: "Контроль сотрудников",
        text: "Отчёты позволяют оценивать эффективность менеджеров, качество обработки заявок, скорость ответа и дисциплину процессов.",
    },
    {
        title: "Анализ и статистика",
        text: "Оценивайте источники лидов, стоимость привлечения, конверсию по этапам, нагрузку команды и качество работы с клиентами.",
    },
];

const advantages = [
    {
        title: "Владеем ключевыми компетенциями Bitrix24",
        text: "Настраиваем CRM, воронки, роботов, задачи, проекты, права доступа, интеграции и бизнес-процессы под реальные задачи компании.",
    },
    {
        title: "Глубоко погружаемся в процессы бизнеса",
        text: "Не ограничиваемся базовой настройкой. Анализируем структуру продаж, коммуникаций и внутренней работы, чтобы система реально помогала бизнесу.",
    },
    {
        title: "Команда с опытом внедрения и автоматизации",
        text: "Над проектом работает команда, которая умеет проектировать CRM-архитектуру, разрабатывать интеграции и сопровождать систему после запуска.",
    },
];

const integrations = [
    {
        title: "Мессенджеры",
        items: [
            {
                title: "WhatsApp",
                icon: whatsappIcon,
            },
            {
                title: "Telegram",
                icon: telegramIcon,
            },
            {
                title: "Jivo",
                icon: jivoIcon,
            },
            {
                title: "Wazzup",
                icon: wazzupIcon,
            },
            {
                title: "Max",
                icon: maxIcon,
            },
        ],
    },
    {
        title: "Телефония",
        items: [
            {
                title: "Мегафон",
                icon: megafonIcon,
            },
            {
                title: "UIS",
                icon: uisIcon,
            },
            {
                title: "Mango Office",
                icon: mangoIcon,
            },
        ],
    },
    {
        title: "Рекламные источники",
        items: [
            {
                title: "Яндекс.Директ",
                icon: yandexDirectIcon,
            },
            {
                title: "Авито",
                icon: avitoIcon,
            },
        ],
    },
    {
        title: "Учет и документы",
        items: [
            {
                title: "1С:Предприятие",
                icon: oneCIcon,
            },
        ],
    },
    {
        title: "Аналитика",
        items: [
            {
                title: "Яндекс.Метрика",
                icon: yandexMetrikaIcon,
            },
            {
                title: "Roistat",
                icon: roistatIcon,
            },
            {
                title: "Calltouch",
                icon: callTouchIcon,
            },
            {
                title: "Google Data Studio",
                icon: googleDataStudioIcon,
            },
        ],
    },
    {
        title: "Хранилища",
        items: [
            {
                title: "Яндекс.Диск",
                icon: yandexDiskIcon,
            },
            {
                title: "Google Drive",
                icon: googleDriveIcon,
            },
            {
                title: "Dropbox",
                icon: dropboxIcon,
            },
        ],
    },
    {
        title: "Банки",
        items: [
            {
                title: "Т.Банк",
                icon: tBankIcon,
            },
            {
                title: "Сбер",
                icon: sberIcon,
            },
            {
                title: "Альба-Банк",
                icon: alphaBankIcon,
            },
        ],
    },
    {
        title: "Эквайринг",
        items: [
            {
                title: "ЮKassa",
                icon: yookassaIcon,
            },
            {
                title: "RoboKassa",
                icon: robokassaIcon,
            },
        ],
    },
];

const steps = [
    {
        title: "Аудит и сбор требований",
        text: "Изучаем текущие процессы компании, точки потерь заявок, структуру отдела продаж и внутренние сценарии работы.",
    },
    {
        title: "Проектирование системы",
        text: "Формируем структуру CRM, воронки, карточки сущностей, роли сотрудников, права доступа и логику автоматизации.",
    },
    {
        title: "Настройка и интеграции",
        text: "Подключаем телефонию, сайт, мессенджеры, рекламу, документы и внешние сервисы, чтобы вся работа велась в одном окне.",
    },
    {
        title: "Автоматизация процессов",
        text: "Настраиваем роботов, триггеры, уведомления, постановку задач и сценарии, которые снимают рутину с команды.",
    },
    {
        title: "Обучение и запуск",
        text: "Показываем сотрудникам, как работать в системе, сопровождаем запуск и корректируем детали после старта.",
    },
];
</script>
